import { AfterViewInit, Directive, ElementRef, HostBinding, HostListener, Input, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl, ValidationErrors, Validator } from '@angular/forms';
import Inputmask from 'inputmask';

// inputmask ships as a CommonJS UMD bundle.
// Depending on the bundler's CJS/ESM interop (esbuild prod build vs. dev server),
// the default export can end up double-wrapped as { default: Inputmask }.
const inputmaskConstructor = (Inputmask as unknown as { default?: typeof Inputmask }).default ?? Inputmask;

@Directive({
    selector: '[mmInputMask]',
    standalone: true,
    exportAs: 'mmInputMask'
})
export class InputMaskDirective implements AfterViewInit, ControlValueAccessor, OnDestroy, OnInit, Validator {
    private elementRef = inject<ElementRef<HTMLInputElement>>(ElementRef);
    private renderer = inject(Renderer2);
    private ngControl = inject(NgControl, { optional: true, self: true });


    @Input() mmInputMask!: string | Inputmask.Options;

    @Input() inputMaskValidationDisabled = false;

    @HostBinding('attr.disabled') inputDisabled: boolean | null = null;

    inputMaskPlugin?: Inputmask.Instance;

    @HostListener('input', ['$event.target.value']) onChange: (v: string) => void = () => {
        // NOOP
    };

    constructor() {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit(): void {
        if (this.ngControl && !this.inputMaskValidationDisabled) {
            const inputMaskValidator = this.validate.bind(this);
            const validators = this.ngControl.control?.validator
                ? [this.ngControl.control.validator, inputMaskValidator]
                : inputMaskValidator;

            this.ngControl.control?.setValidators(validators);
            this.ngControl.control?.updateValueAndValidity();
        }
    }

    ngOnDestroy(): void {
        this.inputMaskPlugin?.remove();
    }

    ngAfterViewInit(): void {
        this.applyInputMask();
        this.renderer.listen(this.elementRef.nativeElement, 'paste', () => {
            setTimeout(() => {
                this.elementRef.nativeElement.dispatchEvent(new Event('input'));
            }, 500);
        });
    }

    writeValue(value: string | null): void {
        this.renderer.setProperty(this.elementRef.nativeElement, 'value', value ?? '');
        this.inputMaskPlugin?.setValue(value ?? '');
    }

    validate(control: AbstractControl): ValidationErrors | null {
        const isValid = this.inputMaskPlugin?.isValid() ?? false;

        return control.value || isValid
            ? this.getErrors(control.errors, false)
            : this.getErrors(control.errors, true);
    }

    getErrors(errors: ValidationErrors | null, addError: boolean): ValidationErrors | null {
        if (addError) {
            return { ...errors, inputMask: true };
        }
        return errors;
    }

    registerOnChange(fn: () => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.inputDisabled = isDisabled || null;
    }

    applyInputMask(): void {
        const opts = typeof this.mmInputMask === 'string' ? { mask: this.mmInputMask } : this.mmInputMask;
        this.inputMaskPlugin = new inputmaskConstructor(opts).mask(this.elementRef.nativeElement);
        this.ngControl?.control?.updateValueAndValidity();
    }

    @HostListener('blur')
    onBlur() {
        this.onTouched();
    }

    onTouched = () => {
        // NOOP
    };
}
